// Frontend telemetry helpers for the recorder. Fire-and-forget POSTs to the
// upload-server, which forwards to Cloud Logging / Grafana Cloud. Never block
// the recording UX on a telemetry failure — all senders swallow errors.
//
// PII rule: do NOT send raw username / email / accessToken values. Caller
// payloads should already use anonymized fields (recordingSessionId,
// hashed identifiers, booleans like has_token).

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const BUILD_ID = process.env.REACT_APP_BUILD_ID || 'dev';

// Denylist applied defensively to every payload before send.
const PII_KEYS = new Set([
  'username',
  'email',
  'displayName',
  'name',
  'accessToken',
  'password',
  'STRAPI_TOKEN',
  'token'
]);

function scrub(value) {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(scrub);
  const out = {};
  for (const [k, v] of Object.entries(value)) {
    if (PII_KEYS.has(k)) continue;
    out[k] = scrub(v);
  }
  return out;
}

function mintRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function send(path, body) {
  try {
    const url = `${API_URL}${path}`;
    const payload = JSON.stringify(body);
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([payload], { type: 'application/json' });
      // sendBeacon returns false if the UA refused to queue; fall through to fetch.
      if (navigator.sendBeacon(url, blob)) return;
    }
    fetch(url, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        'x-telemetry-source': 'recorder',
        'x-request-id': mintRequestId()
      },
      body: payload
    }).catch(() => {});
  } catch (_err) {
    // Never throw from telemetry.
  }
}

export function trackEvent(name, payload = {}) {
  const props = scrub(payload || {});
  send('/api/telemetry/event', {
    name,
    props,
    recording_session_id: payload && payload.recording_session_id ? payload.recording_session_id : undefined,
    build_id: BUILD_ID,
    ts: new Date().toISOString()
  });
}

export function trackError(err, context = {}) {
  const message = err && err.message ? err.message : String(err);
  const stack = err && err.stack ? String(err.stack).slice(0, 4000) : undefined;
  send('/api/telemetry/error', {
    message,
    stack,
    source: 'recorder',
    route: typeof window !== 'undefined' ? window.location.pathname : undefined,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    context: scrub(context || {}),
    recording_session_id: context && context.recording_session_id ? context.recording_session_id : undefined,
    build_id: BUILD_ID,
    ts: new Date().toISOString()
  });
}

export default { trackEvent, trackError };
